import { Button } from '@mui/material'
import { Resource } from 'utoniq-ui'

import {
  FavoriteMemberImageUploadCell,
  FavoriteMemberNameInputCell,
} from '@/components/organisms/console'
import {
  handleError,
  tLabel,
  tMessage,
  useMessage,
  useTranslation,
} from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type Props = {
  onDrop: () => Promise<Resource | null>
  onSubmit: (values: { avatarPath: string; name: string }) => Promise<void>
  isSubmitting: boolean
  progress: number
}

export type FavoriteMemberCreateValues = {
  avatarPath: string | null
  memberName: string
}

const schema: UseFormProps['schema'] = o => ({
  avatarPath: o.string().allow(null),
  memberName: o
    .string()
    .max(50)
    .custom((value, helpers) => {
      // 特殊文字チェック
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel('consoleFavoriteMemberCreate.memberName'),
          }),
        })
      }
      return value
    })
    .label(tLabel('consoleFavoriteMemberCreate.memberName')),
})

export const FavoriteMemberCreateForm: Organism<Props> = ({
  onDrop,
  onSubmit,
  progress,
  isSubmitting,
}) => {
  const { t } = useTranslation('consoleFavoriteMemberCreate')
  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    setError,
    watch,
  } = useForm<FavoriteMemberCreateValues>({
    schema,
    defaultValues: {
      avatarPath: null,
      memberName: '',
    },
  })

  const handleFormSubmit = handleSubmit(values =>
    onSubmit({
      avatarPath: values.avatarPath || '',
      name: values.memberName,
    })
      .then(() => {
        setValue('avatarPath', null, { shouldValidate: true })
        showMessage(t('createSuccess'), 'success')
      })
      .catch(error => {
        handleError<FavoriteMemberCreateValues>(error, setError, showError)
      })
  )

  return (
    <Form
      id="favorite-member-create-form"
      onSubmit={handleFormSubmit}
      sx={{ px: 5 }}
    >
      <FavoriteMemberImageUploadCell
        onDrop={onDrop}
        setValue={setValue}
        onDelete={() => setValue('avatarPath', null, { shouldValidate: true })}
        progress={progress}
        fieldName="avatarPath"
        translationNamespace="consoleFavoriteMemberCreate"
      />
      <FavoriteMemberNameInputCell
        value={watch('memberName')}
        onChange={value => {
          setValue('memberName', value, { shouldValidate: true })
        }}
        error={errors?.memberName}
        translationNamespace="consoleFavoriteMemberCreate"
      />
      <CellFrame sx={{ py: 5, textAlign: 'center' }}>
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          disabled={isSubmitting || !isValid}
          loading={isSubmitting}
        >
          {t('register')}
        </Button>
      </CellFrame>
    </Form>
  )
}
